import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import MessageDialog from "../../components/MessageDialog";
import StorageService from "../../services/StorageService";
import ProjectCategoryService from "../../services/ProjectCategoryService";
import ProjectService from "../../services/ProjectService";
import dprLabourReportService from "../../services/dprLabourReportService";
import utility from "../../utils/utility";
import XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../../assets/images/deviops_logo.png";
import { addPdfLogo } from "../../utils/pdfLogo";

export default function DprLabourReport() {
  const activeCompany = StorageService.activeCompany();
  const companyId = activeCompany?.companyId || "";
  const loginData = StorageService.loginData();

  const employeeId = loginData?.employeeId || "";
  const roleId = loginData?.roleId || "";

  // Filter States
  const [projectCategoryId, setProjectCategoryId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Dropdown Data States
  const [projectCategories, setProjectCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [locations, setLocations] = useState([]);
  const [vendors, setVendors] = useState([]);

  // Report Data States
  const [reports, setReports] = useState([]);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("");

  // Pagination for Detailed Table
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const filteredProjects = projectCategoryId
    ? projects.filter(
        (p) =>
          String(
            p.projectCategoryId || p.categoryId || p.projectCategoryID
          ) === String(projectCategoryId)
      )
    : [];

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
  };

  const toNumber = (val) => {
    const n = Number(val);
    return Number.isFinite(n) ? n : 0;
  };

  const calcTotals = (list) => {
    let skilled = 0;
    let unskilled = 0;
    let total = 0;
    (list || []).forEach((item) => {
      skilled += toNumber(item.skilled);
      unskilled += toNumber(item.unskilled);
      total += toNumber(item.total);
    });
    return { skilled, unskilled, total };
  };

  const loadDropdowns = async () => {
    try {
      let projectCategoryRes = [];
      let projectRes = [];

      try {
        projectCategoryRes = await ProjectCategoryService.getActive();
      } catch (e) {
        console.error("Category API failed", e);
      }

      try {
        projectRes = await ProjectService.getActiveProjectsByEmployee(
          companyId,
          employeeId
        );
      } catch (e) {
        console.error("Project API failed", e);
      }

      setProjectCategories(
        projectCategoryRes?.data || projectCategoryRes || []
      );
      setProjects(projectRes?.data || []);
    } catch (error) {
      console.error("Unexpected error:", error);
    }
  };

  const loadLocationsByProject = async (pId) => {
    if (!pId) {
      setLocations([]);
      return;
    }
    try {
      const res = await dprLabourReportService.getLocationsByProject(pId);
      setLocations(res?.data || []);
    } catch (err) {
      console.error("Location load error:", err);
      setLocations([]);
    }
  };

  const loadVendorsByProject = async (pId) => {
    if (!pId) {
      setVendors([]);
      return;
    }
    try {
      const res = await dprLabourReportService.getVendorsByProject(pId);
      setVendors(res?.data || []);
    } catch (err) {
      console.error("Vendor load error:", err);
      setVendors([]);
    }
  };

  const loadReport = async (pageNumber = page) => {
    if (projectCategoryId || projectId) {
      if (!projectCategoryId) {
        showMessage("Project Category is required");
        return;
      }
      if (!projectId) {
        showMessage("Project Name is required");
        return;
      }
    }

    if (fromDate && !toDate) {
      showMessage("From Date and To Date are required");
      return;
    }

    if ((projectCategoryId || projectId) && (!fromDate || !toDate)) {
      showMessage("From Date and To Date are required");
      return;
    }

    if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
      showMessage("From Date should be less than or equal to To Date");
      return;
    }

    setLoading(true);
    try {
      setReports([]);
      setPage(pageNumber);

      const res = await dprLabourReportService.getReport({
        companyId: companyId || undefined,
        employeeId,
        roleId,
        projectCategoryId: projectCategoryId || undefined,
        projectId: projectId || undefined,
        locationId: locationId || undefined,
        vendorId: vendorId || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        page: pageNumber,
        limit: limit,
      });

      setReports(res?.data || []);
      setTotalPages(res?.pagination?.totalPages || 0);
      setTotalRecords(res?.pagination?.total || 0);
      setPage(pageNumber);
    } catch (error) {
      console.error(error);
      setReports([]);
      showMessage("Error fetching DPR Labour Report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      loadDropdowns();
      const today = new Date().toISOString().split("T")[0];
      setToDate(today);
      loadReport(1);
    }
  }, [companyId]);

  const handleClearFilters = () => {
    setProjectCategoryId("");
    setProjectId("");
    setLocationId("");
    setVendorId("");
    setFromDate("");
    setLocations([]);
    setVendors([]);

    const today = new Date().toISOString().split("T")[0];
    setToDate(today);

    setReports([]);
    setPage(1);
    setTotalPages(0);
    setTotalRecords(0);
    loadReport(1);
  };

  const getAllReportData = async () => {
    try {
      const res = await dprLabourReportService.getReport({
        companyId: companyId || undefined,
        employeeId,
        roleId,
        projectCategoryId: projectCategoryId || undefined,
        projectId: projectId || undefined,
        locationId: locationId || undefined,
        vendorId: vendorId || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        all: true,
      });

      return {
        detailed: res?.data || [],
        locationSummary: res?.locationSummary || [],
        vendorSummary: res?.vendorSummary || [],
      };
    } catch (error) {
      console.error("Export fetch error:", error);
      return { detailed: [], locationSummary: [], vendorSummary: [] };
    }
  };

  const exportExcel = async () => {
    if (!projectCategoryId) {
      showMessage("Project Category is required");
      return;
    }
    if (!projectId) {
      showMessage("Project Name is required");
      return;
    }

    const { detailed, locationSummary, vendorSummary } = await getAllReportData();
    if (!detailed.length && !locationSummary.length && !vendorSummary.length) {
      showMessage("No data to export");
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = {};
    ws["!merges"] = [];

    let projectCategoryLabel = "All";
    if (projectCategoryId) {
      const found = projectCategories.find(
        (pc) => String(pc.id) === String(projectCategoryId)
      );
      projectCategoryLabel = found?.categoryName || projectCategoryId;
    }

    let projectLabel = "All";
    if (projectId) {
      const found = projects.find((p) => String(p.id) === String(projectId));
      projectLabel = found?.projectName || projectId;
    }

    let locationLabel = "All";
    if (locationId) {
      const found = locations.find((l) => String(l.id) === String(locationId));
      locationLabel = found?.locationName || locationId;
    }

    let vendorLabel = "All";
    if (vendorId) {
      const found = vendors.find((v) => String(v.id) === String(vendorId));
      vendorLabel = found?.vendorName || vendorId;
    }

    let row = 0;
    const companyName = activeCompany?.companyName || "Company";

    const thin = { style: "thin" };
    const center = { horizontal: "center", vertical: "center", wrapText: true };

    // Company Name Top Left
    XLSX.utils.sheet_add_aoa(ws, [[companyName]], { origin: { r: row, c: 0 } });
    ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = {
      font: { bold: true, sz: 12 },
    };

    // Report Date Top Right
    XLSX.utils.sheet_add_aoa(
      ws,
      [[`Report Date: ${utility.formatExportDateTime(new Date())}`]],
      { origin: { r: row, c: 7 } }
    );
    ws[XLSX.utils.encode_cell({ r: row, c: 7 })].s = {
      font: { bold: true, sz: 12 },
      alignment: { horizontal: "right", vertical: "center" },
    };

    row += 2;

    // Report Title
    XLSX.utils.sheet_add_aoa(ws, [["DPR Labour Report"]], {
      origin: { r: row, c: 0 },
    });
    ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 7 } });
    ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = {
      font: { bold: true, sz: 16 },
      alignment: { horizontal: "center" },
    };

    row += 2;

    // Filter Info
    XLSX.utils.sheet_add_aoa(
      ws,
      [
        ["Project Category:", projectCategoryLabel, "Project:", projectLabel],
        ["Location:", locationLabel, "Vendor:", vendorLabel],
        [
          "From Date:",
          fromDate ? utility.formatDateDDMMMYYYY(fromDate) : "-",
          "To Date:",
          toDate ? utility.formatDateDDMMMYYYY(toDate) : "-",
        ],
      ],
      { origin: { r: row, c: 0 } }
    );

    const filterLabelStyle = {
      font: { bold: true, sz: 10 },
      alignment: { horizontal: "left", vertical: "center" },
      border: { top: thin, bottom: thin, left: thin, right: thin },
    };
    const filterValueStyle = {
      font: { sz: 10 },
      alignment: { horizontal: "left", vertical: "center" },
      border: { top: thin, bottom: thin, left: thin, right: thin },
    };

    for (let i = 0; i < 3; i++) {
      for (let c = 0; c < 4; c++) {
        const cell = XLSX.utils.encode_cell({ r: row + i, c });
        if (!ws[cell]) continue;
        if ([0, 2].includes(c)) {
          ws[cell].s = filterLabelStyle;
        } else {
          ws[cell].s = filterValueStyle;
        }
      }
    }

    row += 5;

    // 1. Detailed Labour Report Table
    XLSX.utils.sheet_add_aoa(ws, [["1. Detailed Labour Report"]], {
      origin: { r: row, c: 0 },
    });
    ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = {
      font: { bold: true, sz: 12 },
    };
    row++;

    const detailedHeaders = [
      "#",
      "Date",
      "Project",
      "Location",
      "Vendor",
      "Skilled",
      "Unskilled",
      "Total",
    ];
    const detailedHeaderRowIndex = row;
    XLSX.utils.sheet_add_aoa(ws, [detailedHeaders], { origin: { r: row, c: 0 } });
    detailedHeaders.forEach((_, colIdx) => {
      const cell = XLSX.utils.encode_cell({ r: row, c: colIdx });
      ws[cell].s = {
        font: { bold: true, sz: 11 },
        alignment: center,
        fill: { fgColor: { rgb: "E6E6E6" } },
        border: { top: thin, bottom: thin, left: thin, right: thin },
      };
    });
    row++;

    let totalDetSkilled = 0;
    let totalDetUnskilled = 0;
    let grandDetTotal = 0;

    detailed.forEach((item, index) => {
      const sk = toNumber(item.skilled);
      const unsk = toNumber(item.unskilled);
      const tot = toNumber(item.total);
      totalDetSkilled += sk;
      totalDetUnskilled += unsk;
      grandDetTotal += tot;

      const dateSerial = item.reportDate
        ? utility.convertDateToExcelSerial(item.reportDate)
        : null;

      const rowData = [
        index + 1,
        dateSerial !== null
          ? dateSerial
          : item.reportDate
          ? utility.formatDateDMY(item.reportDate)
          : "-",
        item.projectName || "-",
        item.locationName || "-",
        item.vendorName || "-",
        sk,
        unsk,
        tot,
      ];

      XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: { r: row, c: 0 } });
      rowData.forEach((_, colIdx) => {
        const cell = XLSX.utils.encode_cell({ r: row, c: colIdx });
        const isNum = colIdx >= 5;
        const isDate = colIdx === 1;
        const isCenter = colIdx === 0 || colIdx === 1;
        ws[cell].s = {
          alignment: {
            horizontal: isNum ? "right" : isCenter ? "center" : "left",
            vertical: "center",
          },
          border: { top: thin, bottom: thin, left: thin, right: thin },
        };
        if (isNum) {
          ws[cell].t = "n";
          ws[cell].z = "#,##0";
        } else if (isDate && dateSerial !== null) {
          ws[cell].t = "n";
          ws[cell].v = dateSerial;
          ws[cell].z = "dd-mm-yyyy";
        }
      });
      row++;
    });

    if (detailed.length > 0) {
      ws["!autofilter"] = {
        ref: XLSX.utils.encode_range({
          s: { r: detailedHeaderRowIndex, c: 0 },
          e: { r: row - 1, c: detailedHeaders.length - 1 },
        }),
      };
    }

    const detTotalsRow = [
      "",
      "",
      "",
      "",
      "Total:",
      totalDetSkilled,
      totalDetUnskilled,
      grandDetTotal,
    ];
    XLSX.utils.sheet_add_aoa(ws, [detTotalsRow], { origin: { r: row, c: 0 } });
    detTotalsRow.forEach((_, colIdx) => {
      const cell = XLSX.utils.encode_cell({ r: row, c: colIdx });
      if (!ws[cell]) return;
      const isNum = colIdx >= 5;
      ws[cell].s = {
        font: { bold: true, sz: 11 },
        alignment: {
          horizontal: isNum || colIdx === 4 ? "right" : "left",
          vertical: "center",
        },
        border: { top: thin, bottom: thin, left: thin, right: thin },
      };
      if (isNum) {
        ws[cell].t = "n";
        ws[cell].z = "#,##0";
      }
    });

    row += 3;

    // 2. Location Summary Table
    XLSX.utils.sheet_add_aoa(ws, [["2. Location Summary"]], {
      origin: { r: row, c: 0 },
    });
    ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = {
      font: { bold: true, sz: 12 },
    };
    row++;

    const locHeaders = ["#", "Project", "Location", "Skilled", "Unskilled", "Total"];
    XLSX.utils.sheet_add_aoa(ws, [locHeaders], { origin: { r: row, c: 0 } });
    locHeaders.forEach((_, colIdx) => {
      const cell = XLSX.utils.encode_cell({ r: row, c: colIdx });
      ws[cell].s = {
        font: { bold: true, sz: 11 },
        alignment: center,
        fill: { fgColor: { rgb: "E6E6E6" } },
        border: { top: thin, bottom: thin, left: thin, right: thin },
      };
    });
    row++;

    let totalLocSkilled = 0;
    let totalLocUnskilled = 0;
    let grandLocTotal = 0;

    locationSummary.forEach((item, index) => {
      const sk = toNumber(item.skilled);
      const unsk = toNumber(item.unskilled);
      const tot = toNumber(item.total);
      totalLocSkilled += sk;
      totalLocUnskilled += unsk;
      grandLocTotal += tot;

      const rowData = [
        index + 1,
        item.projectName || "-",
        item.locationName || "-",
        sk,
        unsk,
        tot,
      ];

      XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: { r: row, c: 0 } });
      rowData.forEach((_, colIdx) => {
        const cell = XLSX.utils.encode_cell({ r: row, c: colIdx });
        const isNum = colIdx >= 3;
        const isCenter = colIdx === 0;
        ws[cell].s = {
          alignment: {
            horizontal: isNum ? "right" : isCenter ? "center" : "left",
            vertical: "center",
          },
          border: { top: thin, bottom: thin, left: thin, right: thin },
        };
        if (isNum) {
          ws[cell].t = "n";
          ws[cell].z = "#,##0";
        }
      });
      row++;
    });

    const locTotalsRow = ["", "", "Total:", totalLocSkilled, totalLocUnskilled, grandLocTotal];
    XLSX.utils.sheet_add_aoa(ws, [locTotalsRow], { origin: { r: row, c: 0 } });
    locTotalsRow.forEach((_, colIdx) => {
      const cell = XLSX.utils.encode_cell({ r: row, c: colIdx });
      if (!ws[cell]) return;
      const isNum = colIdx >= 3;
      ws[cell].s = {
        font: { bold: true, sz: 11 },
        alignment: {
          horizontal: isNum || colIdx === 2 ? "right" : "left",
          vertical: "center",
        },
        border: { top: thin, bottom: thin, left: thin, right: thin },
      };
      if (isNum) {
        ws[cell].t = "n";
        ws[cell].z = "#,##0";
      }
    });

    row += 3;

    // 3. Vendor Summary Table
    XLSX.utils.sheet_add_aoa(ws, [["3. Vendor Summary"]], {
      origin: { r: row, c: 0 },
    });
    ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = {
      font: { bold: true, sz: 12 },
    };
    row++;

    const venHeaders = ["#", "Project", "Vendor", "Skilled", "Unskilled", "Total"];
    XLSX.utils.sheet_add_aoa(ws, [venHeaders], { origin: { r: row, c: 0 } });
    venHeaders.forEach((_, colIdx) => {
      const cell = XLSX.utils.encode_cell({ r: row, c: colIdx });
      ws[cell].s = {
        font: { bold: true, sz: 11 },
        alignment: center,
        fill: { fgColor: { rgb: "E6E6E6" } },
        border: { top: thin, bottom: thin, left: thin, right: thin },
      };
    });
    row++;

    let totalVenSkilled = 0;
    let totalVenUnskilled = 0;
    let grandVenTotal = 0;

    vendorSummary.forEach((item, index) => {
      const sk = toNumber(item.skilled);
      const unsk = toNumber(item.unskilled);
      const tot = toNumber(item.total);
      totalVenSkilled += sk;
      totalVenUnskilled += unsk;
      grandVenTotal += tot;

      const rowData = [
        index + 1,
        item.projectName || "-",
        item.vendorName || "-",
        sk,
        unsk,
        tot,
      ];

      XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: { r: row, c: 0 } });
      rowData.forEach((_, colIdx) => {
        const cell = XLSX.utils.encode_cell({ r: row, c: colIdx });
        const isNum = colIdx >= 3;
        const isCenter = colIdx === 0;
        ws[cell].s = {
          alignment: {
            horizontal: isNum ? "right" : isCenter ? "center" : "left",
            vertical: "center",
          },
          border: { top: thin, bottom: thin, left: thin, right: thin },
        };
        if (isNum) {
          ws[cell].t = "n";
          ws[cell].z = "#,##0";
        }
      });
      row++;
    });

    const venTotalsRow = ["", "", "Total:", totalVenSkilled, totalVenUnskilled, grandVenTotal];
    XLSX.utils.sheet_add_aoa(ws, [venTotalsRow], { origin: { r: row, c: 0 } });
    venTotalsRow.forEach((_, colIdx) => {
      const cell = XLSX.utils.encode_cell({ r: row, c: colIdx });
      if (!ws[cell]) return;
      const isNum = colIdx >= 3;
      ws[cell].s = {
        font: { bold: true, sz: 11 },
        alignment: {
          horizontal: isNum || colIdx === 2 ? "right" : "left",
          vertical: "center",
        },
        border: { top: thin, bottom: thin, left: thin, right: thin },
      };
      if (isNum) {
        ws[cell].t = "n";
        ws[cell].z = "#,##0";
      }
    });

    ws["!cols"] = [
      { wch: 8 },
      { wch: 14 },
      { wch: 28 },
      { wch: 24 },
      { wch: 28 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
    ];

    const projectCategoryName =
      projectCategories.find((x) => String(x.id) === String(projectCategoryId))
        ?.categoryName || "All";
    const projectNameStr =
      projects.find((x) => String(x.id) === String(projectId))?.projectName ||
      "All";
    const fileName = `DPRLabourRpt_${projectCategoryName}_${projectNameStr}_${utility
      .formatDateDMY(new Date())
      .replace(/\//g, "-")}`;

    XLSX.utils.book_append_sheet(wb, ws, "DPRLabourReport");
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  };

  const exportPdf = async () => {
    if (!projectCategoryId) {
      showMessage("Project Category is required");
      return;
    }
    if (!projectId) {
      showMessage("Project Name is required");
      return;
    }

    const { detailed, locationSummary, vendorSummary } = await getAllReportData();
    if (!detailed.length && !locationSummary.length && !vendorSummary.length) {
      showMessage("No data to export");
      return;
    }

    let projectCategoryLabel = "All";
    if (projectCategoryId) {
      const found = projectCategories.find(
        (pc) => String(pc.id) === String(projectCategoryId)
      );
      projectCategoryLabel = found?.categoryName || projectCategoryId;
    }

    let projectLabel = "All";
    if (projectId) {
      const found = projects.find((p) => String(p.id) === String(projectId));
      projectLabel = found?.projectName || projectId;
    }

    let locationLabel = "All";
    if (locationId) {
      const found = locations.find((l) => String(l.id) === String(locationId));
      locationLabel = found?.locationName || locationId;
    }

    let vendorLabel = "All";
    if (vendorId) {
      const found = vendors.find((v) => String(v.id) === String(vendorId));
      vendorLabel = found?.vendorName || vendorId;
    }

    const doc = new jsPDF("landscape");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const companyName = activeCompany?.companyName || "Company";

    await addPdfLogo(doc, logo, 20, 8, 10, 15);

    doc.setFontSize(11);
    doc.setFont(undefined, "bold");
    doc.text(companyName, 25, 28, { align: "center" });

    const reportDate = utility.formatExportDateTime(new Date());
    const x = pageWidth - 70;
    const y = 18;

    doc.setFontSize(10);
    doc.setFont(undefined, "bold");
    doc.text("Report Date:", x, y);
    doc.setFont(undefined, "normal");
    doc.text(reportDate, x + 24, y);

    const title = "DPR Labour Report";
    doc.setFontSize(18);
    doc.setFont(undefined, "bold");
    doc.text(title, pageWidth / 2, 30, { align: "center" });

    const titleWidth = doc.getTextWidth(title);
    doc.setLineWidth(0.3);
    doc.line(
      (pageWidth - titleWidth) / 2,
      32,
      (pageWidth + titleWidth) / 2,
      32
    );

    doc.setFontSize(9);
    let xLeft = 14;
    let xRight = 115;
    let yHeader = 38;

    doc.setFont(undefined, "bold");
    doc.text("Project Category:", xLeft, yHeader);
    doc.setFont(undefined, "normal");
    doc.text(String(projectCategoryLabel), xLeft + 35, yHeader);

    doc.setFont(undefined, "bold");
    doc.text("Project:", xLeft, yHeader + 5);
    doc.setFont(undefined, "normal");
    doc.text(String(projectLabel), xLeft + 35, yHeader + 5);

    doc.setFont(undefined, "bold");
    doc.text("Location:", xLeft, yHeader + 10);
    doc.setFont(undefined, "normal");
    doc.text(String(locationLabel), xLeft + 35, yHeader + 10);

    doc.setFont(undefined, "bold");
    doc.text("Vendor:", xRight, yHeader);
    doc.setFont(undefined, "normal");
    doc.text(String(vendorLabel), xRight + 35, yHeader);

    doc.setFont(undefined, "bold");
    doc.text("From Date:", xRight, yHeader + 5);
    doc.setFont(undefined, "normal");
    doc.text(
      fromDate ? utility.formatDateDDMMMYYYY(fromDate) : "-",
      xRight + 35,
      yHeader + 5
    );

    doc.setFont(undefined, "bold");
    doc.text("To Date:", xRight, yHeader + 10);
    doc.setFont(undefined, "normal");
    doc.text(
      toDate ? utility.formatDateDDMMMYYYY(toDate) : "-",
      xRight + 35,
      yHeader + 10
    );

    const commonTableStyles = {
      theme: "grid",
      styles: {
        fontSize: 8,
        lineColor: [0, 0, 0],
        lineWidth: 0.15,
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center",
      },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "right",
      },
      didParseCell: function (data) {
        if (data.section === "foot") {
          data.cell.styles.halign = "right";
        }
      },
    };

    let totalDetSkilled = 0, totalDetUnskilled = 0, grandDetTotal = 0;
    detailed.forEach((item) => {
      totalDetSkilled += toNumber(item.skilled);
      totalDetUnskilled += toNumber(item.unskilled);
      grandDetTotal += toNumber(item.total);
    });

    let totalLocSkilled = 0, totalLocUnskilled = 0, grandLocTotal = 0;
    locationSummary.forEach((item) => {
      totalLocSkilled += toNumber(item.skilled);
      totalLocUnskilled += toNumber(item.unskilled);
      grandLocTotal += toNumber(item.total);
    });

    let totalVenSkilled = 0, totalVenUnskilled = 0, grandVenTotal = 0;
    vendorSummary.forEach((item) => {
      totalVenSkilled += toNumber(item.skilled);
      totalVenUnskilled += toNumber(item.unskilled);
      grandVenTotal += toNumber(item.total);
    });

    // 1. Detailed Table
    doc.setFontSize(11);
    doc.setFont(undefined, "bold");
    doc.text("1. Detailed Labour Report", 14, 56);

    const detailedBody = detailed.map((row, index) => [
      index + 1,
      row.reportDate ? utility.formatDateDMY(row.reportDate) : "-",
      row.projectName || "-",
      row.locationName || "-",
      row.vendorName || "-",
      toNumber(row.skilled).toLocaleString(),
      toNumber(row.unskilled).toLocaleString(),
      toNumber(row.total).toLocaleString(),
    ]);

    autoTable(doc, {
      ...commonTableStyles,
      startY: 59,
      margin: { top: 35, left: 14, right: 14, bottom: 25 },
      head: [
        [
          "#",
          "Date",
          "Project",
          "Location",
          "Vendor",
          "Skilled",
          "Unskilled",
          "Total",
        ],
      ],
      body: detailedBody.length ? detailedBody : [["No detailed records found", "", "", "", "", "", "", ""]],
      foot: [
        [
          { content: "Total(s)", colSpan: 5, styles: { halign: "right" } },
          totalDetSkilled.toLocaleString(),
          totalDetUnskilled.toLocaleString(),
          grandDetTotal.toLocaleString(),
        ],
      ],
      showFoot: "lastPage",
      columnStyles: {
        0: { halign: "center", cellWidth: 10 },
        1: { halign: "center", cellWidth: 22 },
        2: { halign: "left" },
        3: { halign: "left" },
        4: { halign: "left" },
        5: { halign: "right", cellWidth: 20 },
        6: { halign: "right", cellWidth: 20 },
        7: { halign: "right", cellWidth: 22 },
      },
    });

    // 2. Location Summary Table
    let nextY = (doc.lastAutoTable?.finalY || 60) + 10;
    if (nextY > pageHeight - 50) {
      doc.addPage();
      nextY = 25;
    }

    doc.setFontSize(11);
    doc.setFont(undefined, "bold");
    doc.text("2. Location Summary", 14, nextY);

    const locBody = locationSummary.map((row, index) => [
      index + 1,
      row.projectName || "-",
      row.locationName || "-",
      toNumber(row.skilled).toLocaleString(),
      toNumber(row.unskilled).toLocaleString(),
      toNumber(row.total).toLocaleString(),
    ]);

    autoTable(doc, {
      ...commonTableStyles,
      startY: nextY + 3,
      margin: { top: 35, left: 14, right: 14, bottom: 25 },
      head: [["#", "Project", "Location", "Skilled", "Unskilled", "Total"]],
      body: locBody.length ? locBody : [["No location records found", "", "", "", "", ""]],
      foot: [
        [
          { content: "Total(s)", colSpan: 3, styles: { halign: "right" } },
          totalLocSkilled.toLocaleString(),
          totalLocUnskilled.toLocaleString(),
          grandLocTotal.toLocaleString(),
        ],
      ],
      showFoot: "lastPage",
      columnStyles: {
        0: { halign: "center", cellWidth: 10 },
        1: { halign: "left" },
        2: { halign: "left" },
        3: { halign: "right", cellWidth: 26 },
        4: { halign: "right", cellWidth: 26 },
        5: { halign: "right", cellWidth: 26 },
      },
    });

    // 3. Vendor Summary Table
    nextY = (doc.lastAutoTable?.finalY || 60) + 10;
    if (nextY > pageHeight - 50) {
      doc.addPage();
      nextY = 25;
    }

    doc.setFontSize(11);
    doc.setFont(undefined, "bold");
    doc.text("3. Vendor Summary", 14, nextY);

    const venBody = vendorSummary.map((row, index) => [
      index + 1,
      row.projectName || "-",
      row.vendorName || "-",
      toNumber(row.skilled).toLocaleString(),
      toNumber(row.unskilled).toLocaleString(),
      toNumber(row.total).toLocaleString(),
    ]);

    autoTable(doc, {
      ...commonTableStyles,
      startY: nextY + 3,
      margin: { top: 35, left: 14, right: 14, bottom: 25 },
      head: [["#", "Project", "Vendor", "Skilled", "Unskilled", "Total"]],
      body: venBody.length ? venBody : [["No vendor records found", "", "", "", "", ""]],
      foot: [
        [
          { content: "Total(s)", colSpan: 3, styles: { halign: "right" } },
          totalVenSkilled.toLocaleString(),
          totalVenUnskilled.toLocaleString(),
          grandVenTotal.toLocaleString(),
        ],
      ],
      showFoot: "lastPage",
      columnStyles: {
        0: { halign: "center", cellWidth: 10 },
        1: { halign: "left" },
        2: { halign: "left" },
        3: { halign: "right", cellWidth: 26 },
        4: { halign: "right", cellWidth: 26 },
        5: { halign: "right", cellWidth: 26 },
      },
    });

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);

      if (i === pageCount) {
        doc.setFont(undefined, "bold");
        doc.setFontSize(8);
        const footerTop = pageHeight - 16;
        doc.text("Prepared By", 25, footerTop);
        doc.text("Checked By", pageWidth / 2, footerTop, { align: "center" });
        doc.text("Approved By", pageWidth - 25, footerTop, { align: "right" });
      }

      doc.setFontSize(9);
      doc.setFont(undefined, "normal");
      doc.text(
        `Page ${i} of ${pageCount}`,
        pageWidth / 2,
        pageHeight - 8,
        { align: "center" }
      );
    }

    const projectCategoryName =
      projectCategories.find((x) => String(x.id) === String(projectCategoryId))
        ?.categoryName || "All";
    const projectNameStr =
      projects.find((x) => String(x.id) === String(projectId))?.projectName ||
      "All";
    const fileName = `DPRLabourRpt_${projectCategoryName}_${projectNameStr}_${utility
      .formatDateDMY(new Date())
      .replace(/\//g, "-")}`;

    doc.save(`${fileName}.pdf`);
  };

  return (
    <>
      <Breadcrumb pageTitle="DPR Labour Report" secondTitle="Reports" />

      <MessageDialog
        message={message}
        type={msgType}
        setMessage={setMessage}
        setMsgType={setMsgType}
      />

      <div className="card p-4 bg-white shadow-sm">
        {/* FILTERS */}
        <div className="row g-3 mb-3">
          <div className="col-md-3">
            <label className="form-label">
              Project Category <span className="text-danger">*</span>
            </label>
            <select
              className="form-select"
              value={projectCategoryId}
              onChange={(e) => {
                const catId = e.target.value;
                setProjectCategoryId(catId);
                setProjectId("");
                setLocationId("");
                setVendorId("");
                setLocations([]);
                setVendors([]);
              }}
            >
              <option value="">Select Project Category</option>
              {projectCategories.map((category) => (
                <option
                  key={category.id || category.projectCategoryId}
                  value={category.id || category.projectCategoryId}
                >
                  {category.categoryName ||
                    category.projectCategoryName ||
                    category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">
              Project Name <span className="text-danger">*</span>
            </label>
            <select
              className="form-select"
              value={projectId}
              onChange={(e) => {
                const selectedProjectId = e.target.value;
                setProjectId(selectedProjectId);
                setLocationId("");
                setVendorId("");

                if (!selectedProjectId) {
                  setFromDate("");
                  setLocations([]);
                  setVendors([]);
                  return;
                }

                loadLocationsByProject(selectedProjectId);
                loadVendorsByProject(selectedProjectId);

                const selectedProject = projects.find(
                  (p) =>
                    String(p.id || p.projectId) === String(selectedProjectId)
                );

                if (selectedProject?.projectStartDate) {
                  const formattedDate = new Date(
                    selectedProject.projectStartDate
                  )
                    .toISOString()
                    .split("T")[0];
                  setFromDate(formattedDate);
                }
              }}
              disabled={!projectCategoryId}
            >
              <option value="">Select Project Name</option>
              {filteredProjects.length === 0 ? (
                <option value="">No Projects Available</option>
              ) : (
                filteredProjects.map((project) => (
                  <option
                    key={project.id || project.projectId}
                    value={project.id || project.projectId}
                  >
                    {project.projectName}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Location</label>
            <select
              className="form-select"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              disabled={!projectId}
            >
              <option value="">All</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.locationName}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Vendor</label>
            <select
              className="form-select"
              value={vendorId}
              onChange={(e) => setVendorId(e.target.value)}
              disabled={!projectId}
            >
              <option value="">All</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.vendorName}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">
              From Date <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              className="form-control"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">
              To Date <span className="text-danger">*</span>
            </label>
            <input
              type="date"
              className="form-control"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
        </div>

        {/* BUTTONS */}
        <div className="mb-3 d-flex justify-content-end align-items-center">
          <div className="d-flex gap-2">
            <button className="btn btn-secondary" onClick={handleClearFilters}>
              <i className="ti ti-refresh me-1"></i>
              Clear
            </button>

            <button
              className="btn btn-primary"
              onClick={() => loadReport(1)}
              disabled={loading}
            >
              <i className="ti ti-search me-1"></i>
              {loading ? "Searching..." : "View Report"}
            </button>
          </div>
        </div>

        <hr />

        {/* EXPORT BUTTONS */}
        <div className="mb-3 d-flex justify-content-end align-items-center">
          <div className="d-flex gap-2">
            <button
              className="btn btn-success"
              onClick={exportPdf}
              disabled={loading}
            >
              <i className="ti ti-file-type-pdf me-1"></i>
              Export PDF
            </button>

            <button
              className="btn btn-success"
              onClick={exportExcel}
              disabled={loading}
            >
              <i className="ti ti-file-spreadsheet me-1"></i>
              Export Excel
            </button>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-striped mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: "5%" }}>#</th>
                <th style={{ width: "11%" }}>Date</th>
                <th>Project</th>
                <th>Location</th>
                <th>Vendor</th>
                <th style={{ width: "10%" }} className="text-end">Skilled</th>
                <th style={{ width: "10%" }} className="text-end">Unskilled</th>
                <th style={{ width: "10%" }} className="text-end">Total</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center">
                    <div className="spinner-border text-primary" />
                  </td>
                </tr>
              ) : reports.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center text-muted">
                    No data found
                  </td>
                </tr>
              ) : (
                reports.map((row, index) => (
                  <tr
                    key={`det-${row.reportDate}-${row.projectId}-${row.locationId}-${row.vendorId}-${index}`}
                  >
                    <td>{(page - 1) * limit + index + 1}</td>
                    <td>{row.reportDate ? utility.formatDateDMY(row.reportDate) : "-"}</td>
                    <td>{row.projectName || "-"}</td>
                    <td>{row.locationName || "-"}</td>
                    <td>{row.vendorName || "-"}</td>
                    <td className="text-end">{toNumber(row.skilled).toLocaleString()}</td>
                    <td className="text-end">{toNumber(row.unskilled).toLocaleString()}</td>
                    <td className="text-end">{toNumber(row.total).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
            {reports.length > 0 && (
              <tfoot className="table-light fw-bold">
                <tr>
                  <td colSpan="5" className="text-end">Page Total:</td>
                  <td className="text-end">{calcTotals(reports).skilled.toLocaleString()}</td>
                  <td className="text-end">{calcTotals(reports).unskilled.toLocaleString()}</td>
                  <td className="text-end">{calcTotals(reports).total.toLocaleString()}</td>
                </tr>
              </tfoot>
            )}
          </table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                Total Records: <strong>{totalRecords}</strong>
              </div>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-secondary"
                  disabled={page === 1}
                  onClick={() => loadReport(page - 1)}
                >
                  Prev
                </button>
                {[...Array(totalPages)]
                  .map((_, i) => i + 1)
                  .filter((p) => p >= page - 2 && p <= page + 2)
                  .map((p) => (
                    <button
                      key={p}
                      className={`btn btn-sm ${
                        page === p ? "btn-primary" : "btn-outline-primary"
                      }`}
                      onClick={() => loadReport(p)}
                    >
                      {p}
                    </button>
                  ))}
                <button
                  className="btn btn-sm btn-secondary"
                  disabled={page === totalPages}
                  onClick={() => loadReport(page + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
